
import { Component, Vue, Watch, Ref } from "vue-property-decorator"
import { Getter, Action } from "vuex-class"

// @ts-ignore
import SkeletonLoader from "@/components/SkeletonLoader.vue"
import UserProfilePicture from "@/components/Account/UserProfilePicture.vue"
import UserSchedules from "@/components/Account/UserSchedules.vue"

import api from "@/services/api"
import User, { CurrentUser, ExtendedPermissions } from "../types/user"
import { Case } from "@/types"
import { UserStatus } from "@/types/userStatus"
import vSelect from "vue-select"
import PageWrapper from "@/components/New/Page/PageWrapper.vue"
import { BTable } from "bootstrap-vue"
import { TenantFeatures } from "@/types/tenantFeatures"

@Component({
	components: {
		"page": PageWrapper,
		"skeleton": SkeletonLoader,
		"v-select": vSelect,
		"user-profile-picture": UserProfilePicture,
		"user-schedules": UserSchedules
	}
})
export default class UserProfile extends Vue {
	@Ref()
	archivedTable!: BTable;

	@Ref()
	archivedContainer!: Element;

	@Getter("getMe") public me!: CurrentUser;

	@Getter("getAvailableUserStatuses") public availableUserStatuses!: UserStatus[];

	@Action("setCurrentUserStatus")
	private setCurrentUserStatus!: (userStatus: UserStatus) => Promise<void>;

	@Action("clearCurrentUserStatus")
	private clearCurrentUserStatus!: () => Promise<void>;

	@Action("loadAvailableUserStatuses")
	private loadAvailableUserStatuses!: () => Promise<void>;

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	private userDetails: User | null = null;
	private activeUserCases: Case[] | null = null;
	private userCases: Case[] | null = null;

	private pageCount: number = 0;
	private resultsCount: number = 0;
	private pageNumber: number = 1;
	private perPage: number = 50;

	isLoadingAll = false
	isLoadingPage = false

	private caseSearchOptions: any[] = [
		{ text: "Assigned", value: false },
		{ text: "Collaborating", value: true }
	];
	private caseSearchOption = false;

	/**
	 * Current user ID from params
	 */
	public get userId (): string {
		return this.$route.params.userId
	}

	public get userStatusSchedulesEnabled ():boolean {
		return this.getIsFeatureEnabled(TenantFeatures.UserStatusSchedules)
	}

	/**
	 * Columns for cases tables
	 */
	public get casesColumns (): any[] {
		return [
			{
				key: "caseNumber",
				label: "Case #"
			},
			{
				key: "title"
			},
			{
				key: "templateTitle",
				label: "Template"
			},
			{
				key: "areas"
			}
		]
	}

	/**
	 * List of active cases - filtered to assignee if caseSearchOption is false
	 */
	public get activeCases (): Case[] | null {
		if (this.caseSearchOption || !this.activeUserCases) {
			return this.activeUserCases
		}

		return this.activeUserCases!.filter(activeCase => activeCase.assigneeId === this.userId)
	}

	private get isMe (): boolean {
		return this.me && this.userId === this.me.id
	}

	/**
	 * On user ID change, update user
	 */
	@Watch("userId")
	public onUserIdChanged () {
		this.userDetails = null
		if (this.userId) {
			this.loadUser()
		}
	}

	/**
	 * On mount, get user details
	 */
	public async mounted () {
		if (this.userId) {
			await this.loadUser()
		}

		this.loadAvailableUserStatuses()
	}

	/**
	 * Fetch user details for current user ID
	 */
	public async loadUser () {
		this.userDetails = await api.userDetails(this.userId)
		this.search()
	}

	/**
	 * Go to case
	 */
	public gotoCase (caseToGoTo: Case): void {
		this.$router.push({ path: `/cases/${caseToGoTo.caseNumber}` })
	}

	public async search () {
		this.isLoadingAll = true
		this.pageNumber = 1

		this.activeUserCases = null
		this.userCases = null
		this.pageCount = 0
		this.resultsCount = 0

		let activePromise = api.userCases(this.userId, this.caseSearchOption, false, 1)

		let archivedPromise = api.userCases(this.userId, this.caseSearchOption, true, 1)

		let [activeRecords, archivedRecords] = await Promise.all([activePromise, archivedPromise])

		// active records
		this.activeUserCases = activeRecords.data

		// archived records
		this.userCases = archivedRecords.data
		this.pageCount = archivedRecords.pageCount
		this.resultsCount = archivedRecords.totalItems

		this.isLoadingAll = false
	}

	public get userHasSetStatusPermission () : boolean {
		return !!(this.me &&
			this.me.extendedPermissions &&
			this.me.extendedPermissions[ExtendedPermissions.SetOtherUsersStatus])
	}

	private async userStatusChanged (userId: string, userStatus: UserStatus) {
		if (this.userDetails) {
			this.userDetails.currentStatus = userStatus
		}

		if (userId === this.me.id) {
			if (userStatus) {
				await this.setCurrentUserStatus(userStatus)
			} else {
				await this.clearCurrentUserStatus()
			}
			return
		}

		if (userStatus) {
			await api.setUserStatus(userId, userStatus.id!)
		} else {
			await api.clearUserStatus(userId)
		}
	}

	/**
	 * @summary Handler which clears the picture URL in our user details.
	 */
	private onUserPictureUpdated () {
		this.userDetails!.pictureUrl = null
	}

	async pageChanged (page: number, fromBottom: boolean = false) {
		this.isLoadingPage = true

		// centre loading spinner
		if (fromBottom) {
			this.scrollToElement(this.archivedTable.$el, { block: "center" })
		}

		let records = await api.userCases(this.userId, this.caseSearchOption, true, page)
		this.userCases = records.data
		this.pageCount = records.pageCount
		this.resultsCount = records.totalItems

		this.isLoadingPage = false

		await this.$nextTick()

		// scroll to top of table heading & pagination
		this.scrollToElement(this.archivedContainer, { behavior: "smooth" })
	}

	private scrollToElement (element: Element, scrollBehavior: {}) {
		if (element) {
			element.scrollIntoView(scrollBehavior)
		}
	}
}
